import { useEffect, useState } from 'react';
import { pdApi } from '../api';
import '../screens.css';
import './HeatMap.css';

/**
 * Opportunities → Scoring → Heat Map.
 *
 * Cloned from `views/opportunities-heatmap.ejs`. Dimension scores across every FULLY
 * scored opportunity, so strengths and gaps read as a block rather than as a list.
 *
 * ONE THING TO KNOW BEFORE READING AN EMPTY COMPONENT AXIS. The source's own heat-map
 * query joins `components.dimension_id` and orders by `dimensions.sort_order` — and
 * NEITHER COLUMN EXISTS in the source's schema, so that screen throws where it runs. This
 * port adds both columns so the screen loads, but `components.dimension_id` is unpopulated
 * (a framework links its components through `framework_components`, not through a column
 * on the component), so the component axis comes back empty.
 *
 * The dimension axis works and is what the screen is actually read for. Whether to
 * populate `components.dimension_id` from the link table — making the component axis work
 * for the first time — is an open decision recorded for the owner, not something to change
 * here on my own judgement.
 */

type Dict = Record<string, unknown>;

/** Score 1–5 to a colour ramp. Red is weak, green is strong. */
function heat(score: number | null): string {
  if (score === null) return '#f1f5f9';
  const stops = ['#ef5350', '#ffa726', '#ffee58', '#9ccc65', '#66bb6a'];
  const idx = Math.min(4, Math.max(0, Math.round(score) - 1));
  return stops[idx]!;
}

export function HeatMap() {
  const [data, setData] = useState<Dict | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setData(await pdApi.scoring.heatmap());
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const opportunities = (data?.opportunities as Dict[]) ?? [];
  const dimensions = (data?.dimensions as Dict[]) ?? [];
  const heatMapData = (data?.heatMapData as Record<string, Dict>) ?? {};

  const scoreFor = (oppId: string, dimensionId: number): number | null => {
    const row = heatMapData[oppId];
    const dims = (row?.dimensions as Dict[]) ?? [];
    const hit = dims.find((d) => Number(d.dimension_id) === dimensionId);
    return hit ? Number(hit.dimension_score) : null;
  };

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">Scoring Heat Map</h1>
          <p className="pd-page-header__subtitle">
            Every fully scored opportunity against every dimension. Red is weak, green is
            strong.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <span className="pd-count">{opportunities.length} scored</span>
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : opportunities.length === 0 ? (
        <div className="pd-empty">
          No opportunity has been fully scored yet — the heat map fills in as scoring
          completes.
        </div>
      ) : (
        <>
          <div className="hm-legend">
            {[1, 2, 3, 4, 5].map((n) => (
              <span key={n} className="hm-legend-item">
                <span className="hm-swatch" style={{ background: heat(n) }} />
                {n}
              </span>
            ))}
          </div>

          <div className="pd-table-wrap">
            <table className="pd-table hm-table">
              <thead>
                <tr>
                  <th style={{ width: 180 }}>Opportunity</th>
                  <th style={{ width: 80 }}>Overall</th>
                  {dimensions.map((d) => (
                    <th key={String(d.id)} title={String(d.description ?? '')}>
                      {String(d.name)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {opportunities.map((o) => {
                  const id = String(o.opportunity_id);
                  return (
                    <tr key={id}>
                      <td>{id}</td>
                      <td className="pd-num">
                        {o.overall_score === null || o.overall_score === undefined
                          ? '—'
                          : Number(o.overall_score).toFixed(2)}
                      </td>
                      {dimensions.map((d) => {
                        const s = scoreFor(id, Number(d.id));
                        return (
                          <td
                            key={String(d.id)}
                            className="hm-cell"
                            style={{ background: heat(s) }}
                            title={`${String(d.name)}: ${s === null ? 'not scored' : s.toFixed(2)}`}
                          >
                            {s === null ? '' : s.toFixed(1)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
